import { useState, useEffect } from 'react';
import {
  Location,
  LocationExternalIDConflictError,
} from '@raydiant/api-client-js';
import Button from '@raydiant/raydial/components/Button';
import Modal from '@raydiant/raydial/components/Modal';
import TextField from '@raydiant/raydial/components/TextField';
import TextArea from '@raydiant/raydial/components/TextArea';
import Dialog, {
  DialogHeader,
  DialogHeading,
  DialogTrigger,
} from '@raydiant/raydial/components/Dialog';
import useUpdateLocation from '../../../../hooks/useUpdateLocation';
import { useToastState } from '@raydiant/raydial/components/Toast';
import { InfoCircle } from '@raydiant/raydial/components/Icons';
import Popover from '@raydiant/raydial/components/Popover';
import Link from '@raydiant/raydial/components/Link';
import config from '../../../../config';
import { useSelector } from 'react-redux';
import { selectUserProfile } from '../../../../selectors/user';
import { selectDomainForCurrentUser } from '../../../../selectors/v2/domains';
import { hasRoleOrHigher } from '../../../../utilities';

const { supportAppUrl } = config;

interface EditLocationDialogProps {
  isOpen: boolean;
  location: Location;
  onSubmit: () => void;
  onClose: () => void;
}

const EditLocationDialog = ({
  isOpen = false,
  location,
  onSubmit,
  onClose,
}: EditLocationDialogProps) => {
  const [name, setName] = useState<string>(location.name);
  const [description, setDescription] = useState<string>(location.description);
  const [externalId, setExternalId] = useState<string>(
    location.externalId || '',
  );
  const [error, setError] = useState<string>('');

  const updateLocation = useUpdateLocation();
  const state = useToastState();

  const currentUser = useSelector(selectUserProfile);
  const domain = useSelector(selectDomainForCurrentUser);

  const handleUpdateLocation = async () => {
    try {
      await updateLocation.mutateAsync({
        locationId: location.id,
        params: {
          name,
          description,
          externalId,
        },
      });
      setName('');
      setDescription('');
      setExternalId('');
      state.add({
        title: 'Location Updated',
        description: 'Location updated successfully',
      });
      onSubmit && onSubmit();
    } catch (err: unknown) {
      if (err instanceof LocationExternalIDConflictError) {
        const existingName = err.existingLocationName;
        const message = `This Location ID is already being used by "${existingName}"`;
        setError(message);
        state.add({
          title: 'LocationID is already used',
          description: message,
          variant: 'caution',
        });
      } else {
        state.add({
          title: 'Location could not be updated',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    }
  };

  useEffect(() => {
    setName(location.name);
    setDescription(location.description);
  }, [location.name, location.description]);

  if (!currentUser) return null;

  const isAdminOrHigher = hasRoleOrHigher('admin', currentUser);
  const isSsoEnabled = domain?.isSsoEnabled;

  return (
    <Modal isOpen={isOpen} className="overflow-auto">
      <Dialog showCloseButton={true} onClose={onClose}>
        <DialogHeader>
          <DialogHeading>
            <span className="text-black">Edit a Location</span>
          </DialogHeading>
        </DialogHeader>

        <div className="p-4">
          <div className="text-black text-[18px] font-semibold">
            Location Information
          </div>
          <div className="flex items-center">
            <div>Enter the details for your location</div>

            <DialogTrigger>
              <Button aria-label="Help" variant="unstyled">
                <div className="flex items-center ml-2 mt-1">
                  <InfoCircle />
                </div>
              </Button>
              <Popover className="max-w-[250px]" placement="bottom start">
                <Dialog>
                  <div className="overflow-hidden">
                    <p>
                      Create a new location to group the screens that reside in
                      the same store/building to keep your screens organized and
                      to allow you to quickly give users access to manage only
                      the screens for this location!
                    </p>
                    <Link
                      className="text-sm mt-2"
                      href={`${supportAppUrl}/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC`}
                    >
                      Learn more
                    </Link>
                  </div>
                </Dialog>
              </Popover>
            </DialogTrigger>
          </div>
        </div>

        <TextField
          value={name || ''}
          onChange={setName}
          placeholder="Location Name"
          description="eg: Austin, TX, Financial District, City Centre Mall, etc."
        />

        <div className="mt-4">
          <TextArea
            value={description || ''}
            onChange={setDescription}
            placeholder="Location Details - Optional"
            description="Add any helpful notes for this location"
            rows={14}
          />
        </div>

        {isAdminOrHigher && isSsoEnabled && (
          <>
            <div className="p-4 mt-4">
              <div className="text-black text-[18px] font-semibold">
                Location ID (for SSO)
              </div>
              <div className="flex items-center">
                <div>
                  This location ID is required for SSO-based automated user
                  provisioning
                </div>

                <DialogTrigger>
                  <Button aria-label="Help" variant="unstyled">
                    <div className="flex items-center ml-2 mt-1">
                      <InfoCircle />
                    </div>
                  </Button>
                  <Popover className="max-w-[250px]" placement="bottom start">
                    <Dialog>
                      <div className="overflow-hidden">
                        <p>
                          If your organization has configured SSO-based user
                          provisioning for Displai, you can input your SSO’s
                          location ID into this field.
                        </p>
                        {/* <Link
                          className="text-sm mt-2"
                          href={`${supportAppUrl}/s/article/Your-Screens-page-1660092388713`}
                        >
                          Learn more
                        </Link> */}
                      </div>
                    </Dialog>
                  </Popover>
                </DialogTrigger>
              </div>
            </div>
            <TextField
              value={externalId || ''}
              onChange={setExternalId}
              placeholder="Location ID"
              description=""
              errorMessage={error}
              validationState={error ? 'invalid' : undefined}
            />
            {/* <div className="px-4">
              <Link
                className="text-sm mt-2"
                href={`${supportAppUrl}/s/article/Your-Screens-page-1660092388713`}
                target="_blank"
                rel="noreferrer"
              >
                Learn more
              </Link>
            </div> */}
          </>
        )}

        <div className="flex flex-col-reverse justify-between mt-8 mb-4 gap-3 px-0 md:gap-6 md:flex-row md:px-4">
          <Button
            onPress={() => onClose()}
            variant="secondary"
            size="large"
            className="grow"
            isDisabled={updateLocation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onPress={() => handleUpdateLocation()}
            variant="primary"
            size="large"
            className="grow"
            isDisabled={updateLocation.isLoading}
            loading={updateLocation.isLoading}
          >
            Edit Location
          </Button>
        </div>
      </Dialog>
    </Modal>
  );
};

export default EditLocationDialog;
